import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  IconButton,
  MenuItem,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import axios from "axios";
import LandingPage from "./page";

export default function UpdateStatusUI({ campaignId = "cmpWB36127443" }) {
  const [open, setOpen] = useState(true);
  const [dateTime, setDateTime] = useState(dayjs());
  const [remarks, setRemarks] = useState("");
  const [status, setStatus] = useState("Pending");
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);

  const handleUpdate = async () => {
    try {
      // Call API
      await axios.put(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/status_update",
        {
          campaignId,
          status,
          remarks,
          dateTime: dateTime.format("DD-MM-YYYY HH:mm:ss"),
        }
      );

      setSuccessSnackbar(true);

      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error) {
      console.error("Error updating status:", error);
      setErrorSnackbar(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = () => {
    setSuccessSnackbar(false);
    setErrorSnackbar(false);
  };

  return (
    <>
      <LandingPage />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: "#1a237e",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
            bgcolor: "#b2ebf2",
          }}
        >
          Update Campaign Status
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Date Picker */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Date & Time:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Select Date & Time"
                  value={dateTime}
                  onChange={setDateTime}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </LocalizationProvider>
            </Box>

            {/* Status Dropdown */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Status:
              </Typography>
              <TextField
                select
                fullWidth
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Box>

            {/* Remarks */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Remarks:
              </Typography>
              <TextField
                placeholder="Enter remarks"
                multiline
                rows={3}
                fullWidth
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              background: "linear-gradient(45deg, #757575 30%, #9e9e9e 90%)",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: 2,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(45deg, #616161 30%, #8e8e8e 90%)",
                boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.3)",
              },
            }}
          >
            Close
          </Button>

          <Button
            onClick={handleUpdate}
            variant="contained"
            sx={{
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: 2,
              boxShadow: "0px 4px 10px rgba(25, 118, 210, 0.3)",
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                boxShadow: "0px 6px 15px rgba(25, 118, 210, 0.4)",
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Success */}
      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Status updated successfully!
        </Alert>
      </Snackbar>

      {/* Snackbar Error */}
      <Snackbar
        open={errorSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          Failed to update status.
        </Alert>
      </Snackbar>
    </>
  );
}
